import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import _ from 'lodash'
import ms from 'ms'
import logger from '@adc/logger'
import { filters, FilterSchema, cognition } from '@adc/blueprint'
import seek, { seekTypes } from '@adc/seek'
import * as graph from '@adc/sdk'
import type {
  CognitionEvaluationGroup,
  CognitionEvaluationTaskName,
} from '@adc/sdk/types'
// TODO: export this from ui-morpheus
import {
  Animate,
  coreHooks,
  LabeledProgressBar,
  PageLayout,
  SelectField,
  Text,
  FilterToolbar,
  utils as btbUtils,
  coreConstants,
  useApplicationNotifications,
  NOTIFICATION_ROLES,
} from '@adc/ui-morpheus'
import type { PaginationState } from '@adc/ui-core'
import DataTable, {
  utils as dataTableUtils,
  SORT_DIRECTION,
  // @ts-ignore cannot find declaration file
} from '@adc/datatable'
import { useRowNavigation } from '@adc/hooks'
import classnames from 'classnames/bind'
import {
  fetchAiEvaluations,
  assignEvaluationGroups,
} from '../../providers/aiEvaluations'
import { ROOT_CRUMB } from '../../constants/routing'
import type { EvaluationFilterConfig } from './filterDefinitions'
import { generateColumns, columnsForDownload } from './columns'
import { definition } from './filterDefinitions'
import EvaluationDetailModal from './evaluationDetailModal'
import BulkAssignModal from './bulkAssignModal'
import ManageAndRunDatasetsModal from './manageAndRunDatasets'
import ManageLLMJudgesModal from './manageLLMJudges'
import OverridePromptModal from './overridePrompt'
import { extractMetrics } from './utils'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const { ColorNames } = coreConstants
const { getInitialPageParams, getUrlParam, setUrlParam } = filters.utils
const {
  useUrlPageStateSyncToUrl,
  useUrlPageStateSyncFromUrl,
  useUrlActionParam,
} = filters.hooks

export const PAGE_SIZE = 25
const DOWNLOAD_PAGE_SIZE = 25
export const DEBOUNCE_DELAY_MS = ms('250ms')
const POST_ASSIGN_DELAY = ms('2s')

const initialDownloadPaginationState = {
  offset: 0,
  pageSize: DOWNLOAD_PAGE_SIZE,
  totalRetrieved: 0,
  totalCount: 0,
  totalPages: 0,
  currentPage: 0,
}

type FilterAppModel = Record<string, unknown>
type FieldConfig = Parameters<seek['fields']>[1]

interface RowData {
  urn: string
  status: graph.enums.CognitionEvaluationGroupStatus
  created_date: string
  created_by: string
}

const { PersistentBrowserStorage } = btbUtils // eslint-disable-line

const TASK_PARAM = 'task'

const AiEvaluationsPage = () => {
  const isMounted = coreHooks.useIsMounted() // eslint-disable-line
  const notifications = useApplicationNotifications() // eslint-disable-line

  /**
   * Initial page params from URL
   */
  const [initialPageParams, setInitialPageParams] = useState(
    getInitialPageParams({ pageSize: PAGE_SIZE })
  )
  const [initialTask] = useState(
    () => getUrlParam(TASK_PARAM) as CognitionEvaluationTaskName | ''
  )

  /**
   * State
   */
  const [rows, setRows] = useState<RowData[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [aggregations, setAggregations] = useState<Record<string, unknown>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [sortColumn, setSortColumn] = useState(() => {
    const { sort } = initialPageParams
    return {
      columnKey: sort?.field ?? 'created_date',
      sortField: sort?.field ?? 'created_date',
      direction: sort?.order?.toLowerCase() ?? (SORT_DIRECTION.DESC as string),
    }
  })
  const [selectedTask, setSelectedTask] = useState<
    CognitionEvaluationTaskName | ''
  >(initialTask ?? '')
  const [browserStorage, setBrowserStorage] = useState(null)
  const [shouldShowZeroState, setShouldShowZeroState] = useState(!initialTask)
  const [currentEvalGroup, setCurrentEvalGroup] =
    useState<CognitionEvaluationGroup | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalOpenTime, setModalOpenTime] = useState(0)
  const saveOccurredRef = useRef(false)
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false)
  const [isManageAndRunDatasetsModalOpen, setIsManageAndRunDatasetsModalOpen] =
    useState(false)
  const [isManageLLMJudgesModalOpen, setIsManageLLMJudgesModalOpen] =
    useState(false)
  const [isOverridePromptModalOpen, setIsOverridePromptModalOpen] =
    useState(false)

  /**
   * Ref to store navigation functions
   */
  const navigationRef = useRef<ReturnType<typeof useRowNavigation> | null>(null)

  /**
   * Search state and ref's
   */
  const [searchText, setSearchText] = useState(
    initialPageParams.searchText || ''
  )
  const searchTextRef = useRef(searchText)
  const setSearchTextRef = useRef(setSearchText)
  searchTextRef.current = searchText
  setSearchTextRef.current = setSearchText

  /**
   * AbortController ref for cancelling in-flight requests
   */
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Download state
   */
  const [downloadPaginationState, setDownloadPaginationState] = useState(
    initialDownloadPaginationState
  )
  const [downloadBatchRows, setDownloadBatchRows] = useState<RowData[]>([])
  const [downloadBatchSuccess, setDownloadBatchSuccess] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadAbort, setDownloadAbort] = useState(false)
  const downloadAbortRef = useRef(downloadAbort)
  downloadAbortRef.current = downloadAbort // for access in async functions

  /**
   * Filter state and definition
   */
  const appState = useMemo(
    () => initialPageParams.filters,
    [initialPageParams.filters]
  )

  const definitionConfig = useMemo(
    () => ({ task: selectedTask }),
    [selectedTask]
  )

  const { data, actions } = filters.hooks.useFilters<
    FilterAppModel,
    FilterSchema.FilterGroupViewModelCollection,
    EvaluationFilterConfig
  >({
    filterState: appState,
    definitionGenerator: definition,
    definitionConfig,
  })

  const dataRef = useRef(data)
  const actionsRef = useRef(actions)
  dataRef.current = data
  actionsRef.current = actions

  /**
   * Pagination hook - must be before useUrlPageStateSyncToUrl
   * Initialized with values from URL if present
   */
  // eslint-disable-next-line
  const {
    Paginator,
    updatePagination,
    resetPagination,
    paginationState,
  }: {
    Paginator: React.ReactNode
    updatePagination: (pageNumber: number) => void
    resetPagination: () => void
    paginationState: PaginationState
  } =
    // eslint-disable-next-line
    coreHooks.useOffsetPagination({
      totalCount,
      pageSize: initialPageParams.pagination?.pageSize ?? PAGE_SIZE,
      initialOffset:
        ((initialPageParams.pagination?.currentPage || 1) - 1) *
        (initialPageParams.pagination?.pageSize ?? PAGE_SIZE),
    })

  /**
   * URL serialization: state -> URL
   */
  useUrlPageStateSyncToUrl({
    filterState: data.filterState,
    sort: {
      field: sortColumn.sortField,
      order:
        sortColumn.direction.toUpperCase() as graph.enums.SortConditionOrder,
    },
    pagination: {
      pageSize: paginationState?.pageSize ?? PAGE_SIZE,
      currentPage: paginationState?.currentPage ?? 1,
    },
    getCompactFilterState: actions.getCompactState || (() => ({})),
    searchText: searchTextRef.current,
  })

  /**
   * URL serialization: URL -> state
   * This is needed for navigation via back/forward buttons
   */
  useUrlPageStateSyncFromUrl({
    onUrlChange: ({ filters, sort, pagination, searchText }) => {
      // Update filters regardless of whether they exist or are cleared
      setInitialPageParams(prev => ({
        ...prev,
        filters: filters || {},
      }))
      if (sort?.field && sort?.order) {
        onSortChange([
          {
            field: sort.field,
            sortField: sort.field,
            sortAscending: sort.order.toUpperCase() === 'ASC',
          },
        ])
      }
      if (pagination?.currentPage) {
        updatePagination(pagination.currentPage)
      }
      if (searchText) {
        setSearchText(searchText)
      }
    },
  })

  // auto open feature
  useUrlActionParam('auto_open', {
    isReady: !isLoading && rows.length > 0,
    onTrigger: value => {
      const index = parseInt(value, 10)
      if (!isNaN(index) && index >= 0 && index < rows.length) {
        onViewClick(rows[index], index)
      }
    },
  })

  // Effect to update URL when task changes
  useEffect(() => {
    setUrlParam(TASK_PARAM, selectedTask || null)
  }, [selectedTask])

  /**
   * Task options for the SelectField
   */
  const taskOptions = useMemo(() => {
    const options = Object.entries(graph.enums.CognitionEvaluationTaskName).map(
      ([, value]) => ({
        text: value
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, l => l.toUpperCase()),
        value,
      })
    )
    return options
  }, [])

  // Memoize aggregations with deep equality check to prevent unnecessary recalculations
  const memoizedAggregations = useMemo(
    () => aggregations,
    [JSON.stringify(aggregations)] // eslint-disable-line
  )

  // Memoize metrics calculation to prevent unnecessary re-renders
  const metrics = useMemo(() => {
    const result = extractMetrics(memoizedAggregations)
    return result
  }, [memoizedAggregations])

  const renderToolbar = useCallback(
    (props: Record<string, unknown>) => {
      return (
        <>
          <FilterToolbar
            searchText={searchTextRef.current}
            setSearchText={setSearchTextRef.current}
            searchPlaceholder='Search by urn, method(version) or tag'
            filterData={dataRef.current}
            filterActions={actionsRef.current}
            {...props}
          />
          <div className={cx('metrics-bar')}>
            <LabeledProgressBar
              label='Ranked'
              tooltipContent='Count of evaluation groups that have been ranked (based on a non-zero net ranking).'
              color={ColorNames.Gray}
              count={metrics.ranked}
              totalCount={metrics.rankedAndUnranked}
              height={10}
              animationSeconds={0.5}
              rounded
              unitMode='both'
            />
            <LabeledProgressBar
              label='Overall Rankings'
              tooltipContent='Cumulative total of net rankings across all types.'
              color={ColorNames.AdcLightGreen}
              count={metrics.sumOfNetRankings}
              percent={Math.floor(
                (metrics.sumOfNetRankings /
                  (metrics.upperBoundNetRanking || 1)) *
                  100
              )}
              height={10}
              animationSeconds={0.5}
              rounded
              unitMode='count'
              centered
            />
            <LabeledProgressBar
              label='Comments'
              tooltipContent='Count of evaluation groups that have been commented on.'
              color={ColorNames.InfoActiveMedium}
              count={metrics.commented}
              totalCount={metrics.commentedAndUncommented}
              height={10}
              animationSeconds={0.5}
              rounded
              unitMode='both'
            />

            <LabeledProgressBar
              label='End User'
              tooltipContent='P / (P+F) based on End User rankings, where P is the count of evaluation groups with a positive net ranking and F is the count with a negative net ranking, based on End User rankings.'
              color={ColorNames.Success}
              count={metrics.endUserPassed}
              totalCount={metrics.endUserPassedAndFailed}
              height={10}
              animationSeconds={0.5}
              rounded
              unitMode='both'
            />
            <LabeledProgressBar
              label='Trusted'
              tooltipContent='P / (P+F) based on Trusted User rankings, where P is the count of evaluation groups with a positive net ranking and F is the count with a negative net ranking, based on Trusted User rankings.'
              color={ColorNames.Success}
              count={metrics.trustedUserPassed}
              totalCount={metrics.trustedUserPassedAndFailed}
              height={10}
              animationSeconds={0.5}
              rounded
              unitMode='both'
            />
            <LabeledProgressBar
              label='Judged'
              tooltipContent='P / (P+F) based on System (automated judge) rankings, where P is the count of evaluation groups with a positive net ranking and F is the count with a negative net ranking, based on System (automated judge) rankings.'
              color={ColorNames.Success}
              count={metrics.systemPassed}
              totalCount={metrics.systemPassedAndFailed}
              height={10}
              animationSeconds={0.5}
              rounded
              unitMode='both'
            />

            <LabeledProgressBar
              label='Overall'
              tooltipContent='P / (P+F) based on overall net rankings, where P is the count of evaluation groups with a positive net ranking and F is the count with a negative net ranking, based on Overall net rankings.'
              color={ColorNames.Success}
              count={metrics.netPassed}
              totalCount={metrics.netPassedAndFailed}
              height={10}
              animationSeconds={0.5}
              rounded
              unitMode='both'
            />
          </div>
        </>
      )
    },
    [metrics]
  )

  // Use row navigation hook
  const navigation = useRowNavigation({
    pageSize: paginationState?.pageSize ?? PAGE_SIZE,
    currentPage: paginationState?.currentPage ?? 1,
    totalPages: paginationState?.totalPages ?? 0,
    // @ts-ignore next
    onPageChange: updatePagination, // eslint-disable-line
    itemCount: rows.length,
    isLoading,
  })

  // Store navigation in ref for use in refocusDataTable
  navigationRef.current = navigation

  // Handle row clicks from DataTable
  const handleRowClick = (_event: unknown, rowIndex: number) => {
    navigation.setActiveItemIndex(rowIndex)
  }

  /**
   * Handlers
   */
  const onRefresh = useCallback(() => {
    setRefreshKey(prevKey => prevKey + 1)
  }, [])

  // TODO: define this in datatable
  type Column = {
    field: string
    sortField: string
    sortAscending: boolean
  }

  const onSortChange = ([column]: Column[]) => {
    setSortColumn({
      columnKey: column.field,
      sortField: column.sortField,
      direction: column.sortAscending
        ? (SORT_DIRECTION.ASC as string) // eslint-disable-line
        : (SORT_DIRECTION.DESC as string), // eslint-disable-line
    })
  }

  // @ts-ignore next - will return to this soon
  const getWinningMethod = /* eslint-disable-line */ (
    evaluations: CognitionEvaluationGroup['evaluations']
  ) => {
    const winningEvaluation = evaluations.find(
      evaluation => evaluation.is_winner
    )
    return winningEvaluation ? winningEvaluation.method : null
  }

  const onViewClick = useCallback(
    (value: unknown, rowIndex: number) => {
      const rowData = value as CognitionEvaluationGroup
      if (!rowData || !rowData.urn || !selectedTask) {
        setCurrentEvalGroup(null)
        logger.warn('Invalid row data or no task selected')
        return
      }

      // Update the navigation index if provided
      if (typeof rowIndex === 'number' && navigationRef.current) {
        navigationRef.current.setActiveItemIndex(rowIndex)
      }

      // Force refetch when opening modal
      setModalOpenTime(Date.now())
      setIsModalOpen(true)
    },
    [selectedTask]
  )

  const onTaskChange = (_event: unknown, data: { value: string }) => {
    setSelectedTask(data.value as CognitionEvaluationTaskName)
    resetPagination() // eslint-disable-line
  }

  // Watch for navigation changes and fetch evaluation data
  useEffect(() => {
    // Skip if modal is not open or no rows
    if (!isModalOpen || !rows.length || !selectedTask) return

    const currentIndex = navigation.activeItemIndex
    if (currentIndex === null || currentIndex === undefined) return

    const currentRow = rows[currentIndex]
    if (!currentRow) return

    // Avoid re-fetching if we already have the data for this row
    // unless the modal was just opened (modalOpenTime changed)
    if (currentEvalGroup?.urn === currentRow.urn && modalOpenTime === 0) return

    const controller = new AbortController()

    const fetchEvaluationData = async () => {
      try {
        setIsLoadingDetail(true)
        const evalGroup = await cognition.utils.getEvaluationGroup({
          controller,
          urn: currentRow.urn,
          task: selectedTask,
          evaluationFilters: {
            methods: null, // get all methods
          },
          retrievalPolicy:
            graph.enums.CognitionEvaluationMessageRetrievalPolicy.Complete,
          viewingFrom: graph.enums.CurrentTargetAudience.Staff,
        })

        if (controller.signal.aborted) return
        setCurrentEvalGroup(evalGroup)
      } catch (error) {
        if (controller.signal.aborted) return
        logger.error('Error fetching evaluation details', error)
        // eslint-disable-next-line
        notifications.toast.open({
          role: NOTIFICATION_ROLES.ERROR,
          title: 'Error fetching evaluation details',
        })
        setCurrentEvalGroup(null)
      } finally {
        if (!controller.signal.aborted) setIsLoadingDetail(false)
      }
    }

    // eslint-disable-next-line
    fetchEvaluationData()

    return () => {
      controller.abort()
    }
  }, [
    navigation.activeItemIndex,
    rows,
    selectedTask,
    isModalOpen,
    currentEvalGroup?.urn,
    notifications,
    modalOpenTime,
  ])

  const handleModalNavigation = useCallback(
    (direction: 'next' | 'previous') => {
      if (!navigationRef.current) return

      // Simply navigate - the useEffect above will handle fetching data
      if (direction === 'next') {
        navigationRef.current.gotoNextItem()
      } else {
        navigationRef.current.gotoPreviousItem()
      }
    },
    []
  )

  // Memoized callbacks for modal to prevent re-renders
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    if (saveOccurredRef.current) {
      onRefresh()
      saveOccurredRef.current = false
    }
  }, [onRefresh, saveOccurredRef])

  const handleOnSave = useCallback(() => {
    logger.info('Save occurred in modal')
    saveOccurredRef.current = true
  }, [])

  const handleModalPrevious = useCallback(() => {
    handleModalNavigation('previous')
  }, [handleModalNavigation])

  const handleModalNext = useCallback(() => {
    handleModalNavigation('next')
  }, [handleModalNavigation])

  /**
   * Computed values
   */
  const columns = useMemo(
    () =>
      generateColumns({
        sortingFilter: {
          columnKey: sortColumn.columnKey,
          direction: sortColumn.direction,
        },
        actionHandlers: {
          onViewClick,
        },
      }),
    [onViewClick, sortColumn.columnKey, sortColumn.direction]
  )

  const fields = useMemo(() => {
    return _.compact([
      ...columns.map(column => column.apiRef),
      'urn',
      'created_by',
      'created_date',
      'status',
      'evaluations.is_winner',
      'fragment:AssigneeDetails',
      'fragment:OwnerDetails',
    ])
  }, [columns])

  // support for reading assignee details - assuming only Party type for now
  const fieldsConfig = useMemo(() => {
    const fieldsConfig: FieldConfig = {
      fragmentDefinitions: {
        AssigneeFields: {
          fragment: graph.gql`fragment AssigneeDetails on CognitionEvaluationGroup {
  assignee {
    ... on Party {
      web_communications {
        identifier
      }
    }
  }
}`,
        },
        OwnerFields: {
          fragment: graph.gql`fragment OwnerDetails on CognitionEvaluationGroup {
  owner {
    ... on Party {
      user {
        email
      }
    }
  }
}`,
        },
      },
    }
    return fieldsConfig
  }, [])

  // eslint-disable-next-line
  coreHooks.useMountEffect(() => {
    // eslint-disable-next-line
    const browserStorage = new PersistentBrowserStorage({
      // eslint-disable-next-line
      adapter: PersistentBrowserStorage.ADAPTERS.LOCAL_STORAGE,
    })
    setBrowserStorage(browserStorage) // eslint-disable-line
  })

  const getDsl = useCallback(() => {
    // eslint-disable-next-line
    const baseDsl = dataTableUtils.buildDSL({
      searchText,
      filterState: { ...data.filterState, task: selectedTask },
      columns,
      filterDefinitions: data.filterDefinitions,
    })

    const taskDslClause = {
      equals: {
        fields: 'task',
        values: [selectedTask],
      },
    }
    const dsl = _.isEqual(baseDsl, { all: {} })
      ? taskDslClause
      : {
          and: {
            expressions: [baseDsl, taskDslClause],
          },
        }
    return dsl
  }, [
    columns,
    searchText,
    data.filterState,
    data.filterDefinitions,
    selectedTask,
  ])

  /**
   * This is the main fetch effect.
   * It will run whenever the sort, search, filter, or pagination state changes.
   */
  // eslint-disable-next-line
  const fetchData = useCallback(
    _.debounce(() => {
      if (!selectedTask) {
        setRows([])
        setAggregations({})
        setTotalCount(0)
        return
      }

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      // Create a new AbortController for this request
      const controller = new AbortController()
      abortControllerRef.current = controller

      setIsLoading(true)

      const dsl = getDsl()

      const aggregationsFields = [
        {
          fields: [
            'default_person_net_ranking',
            'trusted_person_net_ranking',
            'system_net_ranking',
            'net_ranking',
            'comment_count',
          ],
        },
      ]

      fetchAiEvaluations({
        seekType: seekTypes.COGNITION_EVALUATION_GROUPS, // eslint-disable-line
        operationName: 'fetchAiEvaluations',
        dsl, // eslint-disable-line
        fields,
        fieldsConfig,
        sortField: sortColumn.sortField,
        sortDirection: sortColumn.direction,
        paginationState,
        controller,
        aggregations: aggregationsFields,
      })
        .then(result => {
          setTotalCount(result?.data?.total_count || 0)
          // @ts-ignore next line
          setAggregations(result?.data?.aggregation || {})

          if (!result?.data?.content) {
            setRows([])
          } else {
            const rows = result.data.content as unknown as RowData[]
            const safeRows = rows.map(row => row || { urn: 'Error' })
            setRows(safeRows)
          }
          return null
        })
        .catch(error => {
          // Check if the error is due to an aborted request
          if (
            error.name === 'AbortError' ||
            error.message?.includes('aborted')
          ) {
            // This is expected when we cancel a request, don't show error
            logger.info('Fetch request was aborted')
            return
          }

          logger.error('Error fetching AI evaluations', error)
          // eslint-disable-next-line
          notifications.toast.open({
            role: NOTIFICATION_ROLES.ERROR,
            title: 'Error fetching AI evaluations',
          })
          return setRows([])
        })
        .finally(() => {
          // Only set loading to false if this request wasn't aborted
          if (controller.signal && !controller.signal.aborted) {
            setIsLoading(false)
          }
        })
    }, DEBOUNCE_DELAY_MS),
    [
      sortColumn.sortField,
      sortColumn.direction,
      searchText,
      data.filterState,
      columns,
      refreshKey,
      paginationState,
      selectedTask,
      fields,
      fieldsConfig,
      notifications,
    ]
  )

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line
    return () => {
      // Cancel debounced calls
      fetchData.cancel()
      // Cancel any in-flight requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [
    fetchData,
    sortColumn.sortField,
    sortColumn.direction,
    searchText,
    data.filterState,
    columns,
    refreshKey,
    paginationState,
    selectedTask,
  ])

  const handleBulkAssign = useCallback(
    async (assigneeUrn: string, size: number) => {
      try {
        await assignEvaluationGroups({
          assigneeUrn,
          dsl: {
            query: getDsl(),
            sort: [
              {
                field: sortColumn.sortField,
                order: sortColumn.direction.toUpperCase(),
              },
            ],
            size,
          },
        })

        // Refresh the table after successful assignment
        // eslint-disable-next-line
        setTimeout(() => {
          // eslint-disable-next-line
          notifications.toast.open({
            title: `Let's give the index a moment to update...`,
            role: NOTIFICATION_ROLES.INFO,
          })
        }, POST_ASSIGN_DELAY / 2)

        setTimeout(() => {
          onRefresh()
          setIsBulkAssignModalOpen(false)
        }, POST_ASSIGN_DELAY)
      } catch (error) {
        logger.error('Failed to assign evaluation groups', { error })
        throw error
      }
    },
    [
      getDsl,
      notifications.toast, // eslint-disable-line
      onRefresh,
      sortColumn.direction,
      sortColumn.sortField,
    ]
  )

  useEffect(() => {
    if (selectedTask) {
      // Delay hiding zero state to allow fade out animation
      const timer = setTimeout(() => {
        setShouldShowZeroState(false)
      }, ms('1s'))

      return () => clearTimeout(timer)
    } else {
      setShouldShowZeroState(true)
      return undefined
    }
  }, [selectedTask])

  // Reset pagination when filters or search text changes
  const filterStateRef = useRef(data.filterState)
  const searchTextForResetRef = useRef(searchText)

  useEffect(() => {
    // Skip on initial mount
    if (
      filterStateRef.current === data.filterState &&
      searchTextForResetRef.current === searchText
    ) {
      filterStateRef.current = data.filterState
      searchTextForResetRef.current = searchText
      return
    }

    // Check if filter state or search text has changed
    const filtersChanged = !_.isEqual(filterStateRef.current, data.filterState)
    const searchChanged = searchTextForResetRef.current !== searchText

    if (filtersChanged || searchChanged) {
      resetPagination()
    }

    // Update refs
    filterStateRef.current = data.filterState
    searchTextForResetRef.current = searchText
  }, [data.filterState, searchText, resetPagination])

  const fetchDownloadData = useCallback(async () => {
    // Prevent concurrent downloads
    if (isDownloading) {
      logger.warn('Download already in progress, skipping new request')
      return
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create a new AbortController for download requests
    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsDownloading(true)
    setIsLoading(true)

    // Reset state when starting new download
    setDownloadAbort(false)
    setDownloadBatchRows([])
    setDownloadBatchSuccess(false)

    const dsl = getDsl()

    // Recursive function to fetch batches
    const fetchBatch = async (
      currentPaginationState: typeof downloadPaginationState
    ) => {
      // Check abort state before fetching
      if (downloadAbortRef.current) {
        setIsLoading(false)
        setIsDownloading(false)
        logger.info('Download aborted - stopping batch fetch')
        // Reset download state when aborted
        resetDownloadState()
        return
      }

      const fieldsForDownload = [
        ...fields,
        ...columnsForDownload.map(col => col.field),
      ]

      try {
        const result = await fetchAiEvaluations({
          seekType: seekTypes.COGNITION_EVALUATION_GROUPS, // eslint-disable-line
          operationName: 'fetchAiEvaluations',
          dsl,
          // @ts-ignore this - ts is dumb here - doesn't realize its a filled array
          fields: fieldsForDownload,
          fieldsConfig,
          sortField: sortColumn.sortField,
          sortDirection: sortColumn.direction,
          paginationState: currentPaginationState,
          controller,
        })

        const rows = result.data.content as unknown as RowData[]
        const safeRows = rows.map(row => row || { urn: 'Error' })
        const totalCount = result?.data?.total_count || 0

        setDownloadBatchRows(prevRows => [...prevRows, ...safeRows])

        const newTotalRetrieved =
          currentPaginationState.totalRetrieved + safeRows.length
        const newOffset =
          currentPaginationState.offset + currentPaginationState.pageSize
        const hasMoreData =
          newTotalRetrieved < totalCount && safeRows.length > 0

        // Update state
        const newPaginationState = {
          ...currentPaginationState,
          totalRetrieved: newTotalRetrieved,
          totalCount,
          totalPages: result?.data?.total_pages || 0,
          currentPage: currentPaginationState.currentPage + 1,
          offset: newOffset,
        }

        setDownloadPaginationState(newPaginationState)

        // Check if we're done
        if (!hasMoreData || newTotalRetrieved >= totalCount) {
          setDownloadBatchSuccess(true)
          setIsLoading(false)
          setIsDownloading(false)
          return
        }

        // Check abort state again after processing
        if (!downloadAbortRef.current) {
          // Continue with next batch using updated pagination state
          await fetchBatch(newPaginationState)
        } else {
          setIsLoading(false)
          setIsDownloading(false)
          logger.info('Download aborted after processing - cleaning up')
          // Reset download state when aborted
          resetDownloadState()
        }
      } catch (error) {
        // Check if the error is due to an aborted request
        if (
          (error as Error)?.name === 'AbortError' ||
          (error as Error)?.message?.includes('aborted')
        ) {
          // This is expected when we cancel a request, don't show error
          logger.info('Download fetch request was aborted')
          setIsLoading(false)
          setIsDownloading(false)
          return
        }

        setDownloadBatchSuccess(false)
        setDownloadAbort(true)
        logger.error('Error fetching AI evaluations', error)
        // eslint-disable-next-line
        notifications.toast.open({
          role: NOTIFICATION_ROLES.ERROR, // eslint-disable-line
          title: 'Error fetching AI evaluations for download',
        })
        setDownloadBatchRows([])
        setIsLoading(false)
        setIsDownloading(false)
      }
    }

    // Start the recursive fetching with initial state
    // Use a fresh initial state to avoid stale closure issues}
    await fetchBatch(initialDownloadPaginationState)
  }, [
    getDsl,
    fields,
    sortColumn.sortField,
    sortColumn.direction,
    notifications,
    isDownloading,
  ])

  const resetDownloadState = () => {
    setDownloadPaginationState(initialDownloadPaginationState)
    setDownloadBatchRows([])
    setDownloadBatchSuccess(false)
    setDownloadAbort(false)
    setIsDownloading(false)
  }

  const exportData = useCallback(
    (columns: Record<string, unknown>[], data: RowData[]) => {
      // flatten our evaluations and rubrics for CSV export
      const flattenedData: Record<string, unknown>[] = []

      data.forEach(row => {
        const evalGroup = row as unknown as CognitionEvaluationGroup
        if (
          !evalGroup.evaluations ||
          !Array.isArray(evalGroup.evaluations) ||
          evalGroup.evaluations.length === 0
        ) {
          // No evaluations, just push the row as is
          // @ts-ignore next
          flattenedData.push(row)
          return
        }
        evalGroup.evaluations.forEach(evaluation => {
          const baseRow = { ...row, evaluations: evaluation }

          // Now handle rubrics
          if (
            !evaluation.rubrics ||
            !Array.isArray(evaluation.rubrics) ||
            evaluation.rubrics.length === 0
          ) {
            // No rubrics, just push the base row
            flattenedData.push(baseRow)
            return
          }
          evaluation.rubrics.forEach(rubric => {
            const finalRow = {
              ...baseRow,
              evaluations: {
                ...evaluation,
                rubrics: rubric,
              },
            }
            flattenedData.push(finalRow)
          })
        })
      })
      const downloadColumns = [
        ...columns.filter(column => column.id !== 'methods'),
        ...columnsForDownload,
      ]

      // eslint-disable-next-line
      const csvData = dataTableUtils.getCSVExportData(
        downloadColumns,
        flattenedData
      ) as string
      return csvData
    },
    []
  )

  const downloadActions = {
    fetchData: fetchDownloadData,
    reset: resetDownloadState,
    exportData,
    abort: () => {
      setDownloadAbort(true)
      logger.info('Download aborted')
    },
  }

  /**
   * Render
   */
  const breadcrumbs = [ROOT_CRUMB, 'AI Evaluations']

  const bulkAssignMenuOption = {
    key: 'assignForReview',
    text: 'Assign for Review',
    value: 'assign_for_review',
    clickHandler: () => setIsBulkAssignModalOpen(true),
  }

  const manageAndRunDatasetsMenuOption = {
    key: 'manageAndRunDatasets',
    text: 'Manage and Run Datasets',
    value: 'manage_and_run_datasets',
    clickHandler: () => {
      if (!selectedTask) {
        return // this should never happen.
      }
      // eslint-disable-next-line
      const chatSupport = graph.utils.cognition.chatTasks.includes(selectedTask)
      if (!chatSupport) {
        // eslint-disable-next-line
        notifications.toast.open({
          title: 'No Dice!',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          message: (
            <>
              <p>This task does not support chat inputs.</p>
              <p>
                This feature is only currently available for tasks that support
                chat inputs.
              </p>
            </>
          ),
          role: NOTIFICATION_ROLES.CAUTION,
        })
        return
      }
      return setIsManageAndRunDatasetsModalOpen(true)
    },
  }

  const overridePromptMenuOption = {
    key: 'overridePrompt',
    text: 'Override Prompt',
    value: 'override_prompt',
    clickHandler: () => {
      if (!selectedTask) {
        notifications.toast.open({
          title: 'No Task Selected',
          message: 'Please select a task before overriding prompts.',
          role: NOTIFICATION_ROLES.CAUTION,
        })
        return
      }
      return setIsOverridePromptModalOpen(true)
    },
  }

  const manageLLMJudgesMenuOption = {
    key: 'manageLLMJudges',
    text: 'Manage LLM Judges',
    value: 'manage_llm_judges',
    clickHandler: () => {
      if (!selectedTask) {
        notifications.toast.open({
          title: 'No Task Selected',
          message: 'Please select a task before managing LLM Judges.',
          role: NOTIFICATION_ROLES.CAUTION,
        })
        return
      }
      return setIsManageLLMJudgesModalOpen(true)
    },
  }

  return (
    <PageLayout
      cls={{
        container: cx('ai-evaluations-page'),
        content: cx('inner-page-content'),
      }}
      title='AI Evaluations'
      crumbs={breadcrumbs}
      crumbsFullWidth={false}
      buttons={[
        !selectedTask ? null : (
          <div
            key='task-selector-container'
            className={cx('task-selector-container')}
          >
            <Text size='tiny' color='gray' weight='heavy'>
              Task:
            </Text>
            <div className={cx('task-selector')}>
              <SelectField
                hideWrapper
                key='task-selector'
                value={selectedTask}
                onChange={onTaskChange}
                options={taskOptions}
                placeholder='Select a Task'
                compact
                elmId='header_task'
              />
            </div>
          </div>
        ),
      ]}
    >
      <>
        {shouldShowZeroState ? (
          <div className={cx('zero-state')}>
            <Animate.FadeInOut isVisible={!selectedTask} duration={ms('1s')}>
              <div className={cx('zero-state-inner')}>
                <img
                  className={cx('logo')}
                  src='https://adc-tenbox-prod.imgix.net/static/cognition-logo.v1.svg'
                  alt='Cognition Logo'
                />
                <div className={cx('task-selector')}>
                  <SelectField
                    hideWrapper
                    key='task-selector'
                    value={selectedTask}
                    onChange={onTaskChange}
                    options={taskOptions}
                    placeholder='Select a Task'
                    compact
                    elmId='zero_state_task'
                  />
                </div>
              </div>
            </Animate.FadeInOut>
          </div>
        ) : (
          <section className='full-height u-pt-2 u-mr-4 u-ml-4'>
            {isMounted && (
              <DataTable
                columns={columns}
                isLoading={isLoading || isLoadingDetail}
                rowData={rows}
                customToolbar={renderToolbar}
                refreshData={onRefresh}
                entityKey='AI_EVALUATIONS'
                primaryKeyField='urn'
                sizes={{
                  rowHeight: 40,
                  headerHeight: 40,
                  footerHeight: 110,
                }}
                customTableFooter={() => (
                  <div className='u-mt-1'>{Paginator}</div>
                )}
                changeSortBy={onSortChange}
                hideTableHeader
                browserStorage={browserStorage}
                onRowClick={handleRowClick}
                activeRowIndex={navigation.activeItemIndex}
                totalCount={totalCount}
                onKeyboardNavigation={{
                  gotoNextItem: navigation.gotoNextItem,
                  gotoPreviousItem: navigation.gotoPreviousItem,
                }}
                allowExportCSV
                downloadActions={downloadActions}
                downloadData={{
                  content: downloadBatchRows,
                  totalCount: downloadPaginationState.totalCount,
                  totalRetrieved: downloadBatchRows.length,
                  isBatchSuccessful: downloadBatchSuccess,
                }}
                customMenuOptions={[
                  bulkAssignMenuOption,
                  manageAndRunDatasetsMenuOption,
                  overridePromptMenuOption,
                  manageLLMJudgesMenuOption,
                ]}
              />
            )}
          </section>
        )}
        {/* @ts-ignore next - TS complaining about connected component */}
        <EvaluationDetailModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSave={handleOnSave}
          evaluationData={currentEvalGroup}
          currentIndex={
            (paginationState.offset || 0) +
            (navigationRef.current?.activeItemIndex || 0)
          }
          totalCount={totalCount}
          onPrevious={handleModalPrevious}
          onNext={handleModalNext}
          hasPrevious={!navigationRef.current?.isFirstItem}
          hasNext={!navigationRef.current?.isLastItem}
          isLoading={isLoading || isLoadingDetail}
        />
        <BulkAssignModal
          isOpen={isBulkAssignModalOpen}
          onClose={() => setIsBulkAssignModalOpen(false)}
          onAssign={handleBulkAssign}
          totalFilteredCount={totalCount}
        />
        <ManageAndRunDatasetsModal
          isOpen={isManageAndRunDatasetsModalOpen}
          onClose={() => setIsManageAndRunDatasetsModalOpen(false)}
          task={selectedTask as CognitionEvaluationTaskName}
        />
        <OverridePromptModal
          isOpen={isOverridePromptModalOpen}
          onClose={() => setIsOverridePromptModalOpen(false)}
          task={selectedTask as CognitionEvaluationTaskName}
        />
        <ManageLLMJudgesModal
          isOpen={isManageLLMJudgesModalOpen}
          onClose={() => setIsManageLLMJudgesModalOpen(false)}
          task={selectedTask as CognitionEvaluationTaskName}
        />
      </>
    </PageLayout>
  )
}

export default AiEvaluationsPage
